import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  Icon,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { ObservationLog, UserGroups } from '@/types'
import { AuthorizationGuard, LinkButton } from '@/components'
import { getSpeciesWithResearchTypesString } from '@/utils/getSpeciesWithResearchTypes'
import { getPath } from '@/utils'
import dayjs from 'dayjs'
import { DATE_FORMAT_DMY_TIME, PageReferrers, ROUTES_PATH } from '@/constants'
import { renderSpecieWithOriginal } from '@/features/observationLogs/utils/renderSpecieWithOriginal'
import { useAuth } from '@/hooks'

export type ObservationLogsTableBodyProps = {
  rows: ObservationLog[]
  showObservationLogDetails: (id: number) => void
  selectedIds: number[]
  onToggleSelect: (id: number, isApproved: boolean) => void
}

export const renderStringFromList = (
  amount: number | string | null,
  amount2: number | string | null,
  amount3: number | string | null,
  amount4: number | string | null
) => [amount, amount2, amount3, amount4].filter((s) => !!s).join(', ')

export const renderObservedSpecie = (
  specie: string | null,
  specie2: string | null,
  specie3: string | null,
  specie4: string | null,
  other_specie: string | null,
  unknown_specie?: string | null
) => {
  if (other_specie) {
    return other_specie
  }

  if (unknown_specie) {
    return unknown_specie
  }

  return [specie, specie2, specie3, specie4].filter((s) => !!s).join(', ')
}

export const renderBehaviour = (
  behaviour: string | null,
  other_behaviour: string | null
) => (other_behaviour ? other_behaviour : behaviour || '')

export const renderPlaceTypeFound = (
  is_place_found: boolean,
  place_type: string | null
) => (is_place_found ? place_type || '' : '-')

export const ObservationLogsTableBody = ({
  rows,
  showObservationLogDetails,
  selectedIds,
  onToggleSelect,
}: ObservationLogsTableBodyProps): JSX.Element => {
  const { t } = useTranslation('observationLogs')
  const auth = useAuth()

  const isClient = auth?.user?.groups?.includes(UserGroups.CLIENT) || false

  return (
    <TableBody>
      {rows?.map(
        ({
          id,
          research_visit_event,
          research_observation_log_type,
          observed_at,
          created_by,
          specie,
          specie2,
          specie3,
          specie4,
          unknown_specie,
          original_specie,
          original_specie2,
          original_specie3,
          original_specie4,
          other_specie,
          amount,
          amount2,
          amount3,
          amount4,
          behaviour,
          other_behaviour,
          is_place_found,
          place_type,
          is_approved,
        }) => (
          <TableRow key={id} selected={selectedIds.includes(id)}>
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                checked={selectedIds.includes(id)}
                onChange={() => onToggleSelect(id, is_approved)}
              />
            </TableCell>
            <TableCell>{id}</TableCell>
            <TableCell component="th" scope="row">
              {research_visit_event?.research_area?.project?.client_name ?? '-'}
            </TableCell>
            <TableCell>
              {research_visit_event?.research_area?.project?.name ?? '-'}
            </TableCell>
            <TableCell>
              {research_visit_event?.research_area?.project?.project_number ??
                '-'}
            </TableCell>
            <TableCell>
              {research_visit_event?.research_area?.id ?? '-'}
            </TableCell>
            <TableCell>
              {research_visit_event?.research_area?.location_name ?? '-'}
            </TableCell>
            <TableCell>
              {!!research_visit_event?.id && (
                <AuthorizationGuard
                  allowedGroups={[UserGroups.MANAGER]}
                  redirect={false}
                  fallback={research_visit_event?.round_name}
                >
                  <LinkButton
                    url={getPath(ROUTES_PATH.MONITORING_DETAIL, {
                      id: research_visit_event.id,
                    })}
                    title={`${research_visit_event?.round_name}`}
                    referrer={PageReferrers.OBSERVATION}
                    arrowIcon={false}
                    variant="textThin"
                  />
                </AuthorizationGuard>
              )}
            </TableCell>
            <TableCell>
              {getSpeciesWithResearchTypesString(research_visit_event)}
            </TableCell>
            <TableCell>{research_observation_log_type.name}</TableCell>
            <TableCell>
              {observed_at
                ? dayjs(observed_at).format(DATE_FORMAT_DMY_TIME)
                : '-'}
            </TableCell>
            <TableCell>
              {created_by
                ? `${created_by.first_name} ${created_by.last_name}`
                : '-'}
            </TableCell>
            <TableCell>
              {renderObservedSpecie(
                renderSpecieWithOriginal(specie, original_specie, isClient),
                renderSpecieWithOriginal(specie2, original_specie2, isClient),
                renderSpecieWithOriginal(specie3, original_specie3, isClient),
                renderSpecieWithOriginal(specie4, original_specie4, isClient),
                other_specie,
                unknown_specie
              )}
            </TableCell>
            <TableCell>
              {renderStringFromList(amount, amount2, amount3, amount4)}
            </TableCell>
            <TableCell>{renderBehaviour(behaviour, other_behaviour)}</TableCell>
            <TableCell>
              {renderPlaceTypeFound(is_place_found, place_type)}
            </TableCell>
            <TableCell>
              {is_approved ? t(`observation-log.yes`) : t(`observation-log.no`)}
            </TableCell>
            <TableCell align="right">
              <Button
                variant="text"
                onClick={() => showObservationLogDetails(id)}
                endIcon={
                  <Icon
                    sx={{
                      fontSize: 14,
                      lineHeight: '21px',
                      fontWeight: 600,
                    }}
                  >
                    <ArrowForwardIcon sx={{ height: 14, width: 14 }} />
                  </Icon>
                }
              >
                <Typography variant="body2" fontWeight={600}>
                  {t('buttons.view')}
                </Typography>
              </Button>
            </TableCell>
          </TableRow>
        )
      )}
    </TableBody>
  )
}
